import { createAction, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { Block, BlockType } from 'src/config/api/models/Block';
import {
  fetchBlocks,
  patchBlock,
  addBlock,
  deleteBlock,
  toggleBlockIsPrintable,
  fetchExercises,
  patchCluster,
  createCluster,
  deleteCluster,
  reorderBlocks,
  releaseCluster,
  splitCluster,
  createSection,
  deleteSection,
} from '../api/blocks';
import { store } from '../../../store';
import { Exercise } from 'src/config/api/models/Exercise';

type NotificationErrorMessage =
  | {
      text: string;
      mode: 'api' | 'debt';
    }
  | undefined;

export interface BlocksState {
  blocks?: Block[];
  pageId?: string;
  exercises?: Exercise[];
  loading: boolean;
  error?: boolean;
  errorMessage?: NotificationErrorMessage;
  successMessage?: string;
  success?: boolean;
  hoverBlock?: string | undefined;
  targetBlock?: string | undefined;
  focusBlock?: string | undefined;
  exerciseToDuplicate?: { newId?: string; originalUrl: string };
  nextBlock?: string | undefined;
  blockToDelete?: string;
  selectedBlock?: string;
  debugMode?: boolean;
  blockToScroll: null | Block['id'];
  editingClusterOrSection: boolean;
}

const initialState: BlocksState = {
  loading: false,
  blocks: undefined,
  pageId: undefined,
  exercises: undefined,
  error: undefined,
  errorMessage: undefined,
  successMessage: undefined,
  success: undefined,
  hoverBlock: undefined,
  targetBlock: undefined,
  focusBlock: undefined,
  exerciseToDuplicate: undefined,
  nextBlock: undefined,
  blockToDelete: undefined,
  selectedBlock: undefined,
  debugMode: false,
  blockToScroll: null,
  editingClusterOrSection: false,
};

const resetSuccess = createAction('blocks/resetSuccess');
const cleanStateAfterSuccess = createAction('blocks/cleanStateAfterSuccess');

export const blocksSlice = createSlice({
  name: 'blocks',
  initialState,
  reducers: {
    setDebugMode: (state, action: PayloadAction<boolean>) => {
      state.debugMode = action.payload;
    },
    switchDebugMode: (state) => {
      const currentDebugModeState = state.debugMode;
      state.debugMode = !currentDebugModeState;
    },

    //************************** Block List ****************************//

    updateBlocksListViaId: (state, action: PayloadAction<string[]>) => {
      state.blocks = action.payload.map(
        (blockId) => state.blocks!.find((block) => block.id === blockId)!
      );
    },
    updateBlocksList: (state, action: PayloadAction<Block[]>) => {
      state.blocks = action.payload;
    },

    //************************** Blocks *****************************//

    updateBlock: (state, action: PayloadAction<Block>) => {
      const { id } = action.payload;
      state.blocks = state.blocks?.map((block) => {
        if (block.id === id) {
          return action.payload;
        }
        return block;
      });
    },
    createBlock: (state, action: PayloadAction<Block>) => {
      const oldBlocks = [...state.blocks!];
      oldBlocks.push(action.payload);
      state.blocks = oldBlocks;

      if (state.exerciseToDuplicate?.originalUrl) {
        state.exerciseToDuplicate.newId = action.payload.id;
      }
    },
    removeBlock: (state, action: PayloadAction<string>) => {
      const oldBlocks = [...state.blocks!];
      const remainingBlocks = oldBlocks.filter((block) => block.id !== action.payload);
      state.blocks = remainingBlocks;
    },
    setHoverBlock: (state, action: PayloadAction<string | undefined>) => {
      state.hoverBlock = action.payload;
    },
    setTargetBlock: (state, action: PayloadAction<string | undefined>) => {
      state.targetBlock = action.payload;
    },
    setFocusBlock: (state, action: PayloadAction<string | undefined>) => {
      state.focusBlock = action.payload;
    },
    setNextBlock: (state, action: PayloadAction<string | undefined>) => {
      state.nextBlock = action.payload;
    },
    setBlockToDelete: (state, action: PayloadAction<string | undefined>) => {
      state.blockToDelete = action.payload;
    },
    setSelectedBlock: (state, action: PayloadAction<string | undefined>) => {
      state.selectedBlock = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<NotificationErrorMessage | undefined>) => {
      state.errorMessage = action.payload;
    },

    //************************** Exercises ****************************/

    updateExercisesOrder: (state: BlocksState, action: PayloadAction<Exercise[]>) => {
      state.exercises = action.payload;
    },
    setExerciseToDuplicate: (
      state,
      action: PayloadAction<{ newId?: string; originalUrl: string }>
    ) => {
      state.exerciseToDuplicate = action.payload;
    },
    clearExerciseToDuplicate: (state) => {
      state.exerciseToDuplicate = undefined;
    },
    setBlockToScroll: (state, action: PayloadAction<BlocksState['blockToScroll']>) => {
      state.blockToScroll = action.payload;
    },
    setIsLoadingForClusterOrSectio: (
      state,
      action: PayloadAction<BlocksState['editingClusterOrSection']>
    ) => {
      state.editingClusterOrSection = action.payload;
    },
  },

  //************************** API ****************************/

  extraReducers: (builder) => {
    // #region fetch blocks
    builder.addCase(fetchBlocks.pending, (state, { meta }) => {
      state.loading = true;
      state.blocks = undefined;
      state.error = undefined;
    });
    builder.addCase(fetchBlocks.fulfilled, (state, action) => {
      state.loading = false;
      state.blocks = action.payload;
      state.error = undefined;
      state.success = true;
      state.pageId = action.meta.arg;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(fetchBlocks.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    // #endregion

    // #region fetch exercises
    builder.addCase(fetchExercises.pending, (state) => {
      state.loading = true;
      state.blocks = undefined;
      state.error = undefined;
    });
    builder.addCase(fetchExercises.fulfilled, (state, action) => {
      state.exercises = action.payload;
      state.loading = false;
      state.error = undefined;
      state.errorMessage = undefined;
      state.success = true;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(fetchExercises.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = { text: action.error.message as string, mode: 'api' };
    });
    // #endregion

    // #region add block
    builder.addCase(reorderBlocks.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(reorderBlocks.fulfilled, (state, action) => {
      state.loading = false;
      state.error = undefined;
      state.errorMessage = undefined;
      state.success = true;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(reorderBlocks.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.success = false;
      state.errorMessage = { text: 'Error reordering blocks... Restoring', mode: 'api' };
      const existingPageId = state.pageId;
      if (existingPageId) {
        setTimeout(() => store.dispatch(fetchBlocks(existingPageId)), 100);
      }
    });
    // #endregion

    // #region Patch block
    builder.addCase(patchBlock.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(patchBlock.fulfilled, (state, action) => {
      const { id } = action.payload;
      if (!action.meta.arg.dontUpdateBlocks) {
        state.blocks = state.blocks?.map((block) => {
          if (block.id === id) {
            return action.payload as Block;
          } else if (block.type === 'cluster' && !!block.sections) {
            let sectionsCopy = [...block.sections];
            sectionsCopy = sectionsCopy?.map((section) => {
              let childBlocksCopy = [...(section.childBlocks || [])];
              childBlocksCopy = childBlocksCopy.map((childBlock) => {
                if (childBlock.id === id) return action.payload as Block;
                return childBlock;
              });
              section.childBlocks = childBlocksCopy;
              return section;
            });
            block.sections = sectionsCopy;
            return block;
          }
          return block;
        });

        state.exercises = state.exercises?.map((exercise) => {
          if (exercise.id === id) {
            return action.payload as Exercise;
          }
          return exercise;
        });
      }
      state.loading = false;
      state.error = undefined;
      state.errorMessage = undefined;
      state.success = true;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(patchBlock.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = { text: 'Error modifying block...', mode: 'api' };
      state.success = false;
    });
    // #endregion

    // #region add block
    builder.addCase(addBlock.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(addBlock.fulfilled, (state, action) => {
      if (action.payload.isExercisePage) {
        const oldExercises = [...state.exercises!];
        const newExercise = action.payload as Exercise;
        oldExercises.splice(newExercise.position?.valueOf() || 0, 0, newExercise);
        state.exercises = oldExercises;
      } else {
        const oldBlocks = [...state.blocks!];
        const newBlock = action.payload as Block;
        const clusterId = action.payload.cluster_id;
        const sectionId = action.payload.section_id;
        const previousBlockId = action.payload.previous_block_id;
        if (clusterId && sectionId) {
          const cluster = oldBlocks.find((c) => c.id === clusterId) as Block;
          const clusterIndex = oldBlocks.indexOf(cluster);
          const section = cluster.sections!.find((s) => s.id === sectionId) as Block;
          const sectionIndex = cluster.sections?.indexOf(section);
          const foundPreviousBlockInSection = section.childBlocks!.find(
            (b) => b.id === previousBlockId
          ) as Block;
          const newBlockIndex = section.childBlocks!.indexOf(foundPreviousBlockInSection) + 1;
          const newSectionChildren = [...(section.childBlocks || [])];
          newSectionChildren.splice(newBlockIndex, 0, newBlock);
          section.childBlocks = newSectionChildren;
          cluster.sections![sectionIndex!] = section;
          oldBlocks.splice(clusterIndex, 1, cluster);
        } else {
          const foundPreviousBlockInPage = oldBlocks.find((b) => b.id === previousBlockId) as Block;
          const newBlockIndex = oldBlocks.indexOf(foundPreviousBlockInPage) + 1;
          oldBlocks.splice(newBlockIndex, 0, newBlock);
        }
        state.blocks = oldBlocks;
      }

      if (action.payload.type === BlockType.WYSIWYG) state.selectedBlock = action.payload.id;

      if (state.exerciseToDuplicate?.originalUrl) {
        state.exerciseToDuplicate.newId = action.payload.id;
      }

      state.loading = false;
      state.error = undefined;
      state.success = true;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(addBlock.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    // #endregion

    // #region delete block
    builder.addCase(deleteBlock.pending, (state, { meta }) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(deleteBlock.fulfilled, (state, action) => {
      let oldBlocks = [...(state.blocks || [])];
      let oldExercises = [...(state.exercises || [])];
      const clusterId = action.payload.cluster_id;
      const sectionId = action.payload.section_id;

      if (clusterId && sectionId) {
        const blocksCluster = oldBlocks.find((c) => c.id === clusterId) as Block;
        const blocksClusterIndex = oldBlocks.indexOf(blocksCluster);

        const blocksSection = blocksCluster.sections!.find((s) => s.id === sectionId) as Block;
        const blocksSectionIndex = blocksCluster.sections!.indexOf(blocksSection);

        const newSectionBlocks = blocksSection.childBlocks!.filter(
          (b) => b.id !== action.payload.id
        );
        blocksSection.childBlocks = newSectionBlocks;
        blocksCluster.sections![blocksSectionIndex] = blocksSection;

        oldBlocks.splice(blocksClusterIndex, 1, blocksCluster);
      } else {
        // remove block from page
        oldBlocks = oldBlocks.filter((block) => block.id !== action.payload.id);
      }
      oldExercises = oldExercises.filter((exercise) => exercise.id !== action.payload.id);

      state.exercises = oldExercises;
      state.blocks = oldBlocks;
      state.loading = false;
      state.error = undefined;
      state.success = true;
      state.selectedBlock = undefined;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(deleteBlock.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.success = false;
      state.errorMessage = { text: 'Problem deleting block... Restoring', mode: 'api' };
      const existingPageId = state.pageId;
      if (existingPageId) {
        setTimeout(() => store.dispatch(fetchBlocks(existingPageId)), 2000);
      }
    });
    // #endregion

    // #region toggleBlockIsPrintable block
    builder.addCase(toggleBlockIsPrintable.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(toggleBlockIsPrintable.fulfilled, (state, action) => {
      const { id } = action.payload;
      state.blocks = state.blocks?.map((block) => {
        if (block.id === id) {
          return action.payload;
        }
        return block;
      });

      state.loading = false;
      state.error = undefined;
      state.success = true;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(toggleBlockIsPrintable.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    // #endregion

    /********************* CLUSTERS **************************/

    // #region Patch cluster
    builder.addCase(patchCluster.pending, (state) => {
      state.editingClusterOrSection = true;
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(patchCluster.fulfilled, (state, action) => {
      const { id } = action.payload;
      state.blocks = state.blocks?.map((block) => {
        if (block.id === id) {
          return action.payload as Block;
        }
        return block;
      });

      state.loading = false;
      state.error = undefined;
      state.success = true;
      state.editingClusterOrSection = false;

      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(patchCluster.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.success = false;
      state.editingClusterOrSection = false;
    });
    // #endregion

    // #region Create cluster
    builder.addCase(createCluster.pending, (state) => {
      state.editingClusterOrSection = true;
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(createCluster.fulfilled, (state, action) => {
      const stateBlocks = state.blocks ?? [];
      const oldBlocks = [...stateBlocks];
      const beforeClusterId = action.meta.arg.beforeBlockId;
      const newCluster = action.payload;
      const indexBefore = oldBlocks.findIndex((b) => b.id === beforeClusterId) + 1;
      oldBlocks.splice(
        indexBefore,
        (newCluster.sections !== undefined && newCluster.sections[0].childBlocks?.length) || 0,
        newCluster
      );

      state.blocks = oldBlocks;
      state.loading = false;
      state.error = undefined;
      state.success = true;
      state.editingClusterOrSection = false;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(createCluster.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.success = false;
      state.errorMessage = { text: 'Problem creating cluster... Restoring', mode: 'api' };
      const existingPageId = state.pageId;
      state.editingClusterOrSection = false;
      if (existingPageId) {
        setTimeout(() => store.dispatch(fetchBlocks(existingPageId)), 2000);
      }
      setTimeout(() => (state.editingClusterOrSection = true), 1000);
    });
    // #endregion

    // #region Split cluster
    builder.addCase(splitCluster.pending, (state) => {
      state.editingClusterOrSection = true;
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(splitCluster.fulfilled, (state, action) => {
      state.editingClusterOrSection = false;
      const stateBlocks = state.blocks ?? [];
      const oldBlocks = [...stateBlocks];
      const clusterId = action.meta.arg.clusterId;
      const existingClusterIndex = oldBlocks.findIndex((b) => b.id === clusterId);
      oldBlocks.splice(
        existingClusterIndex,
        1,
        action.payload.oldCluster,
        action.payload.newCluster
      );
      state.blocks = oldBlocks;
      state.loading = false;
      state.error = undefined;
      state.success = true;

      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(splitCluster.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.success = false;
      state.editingClusterOrSection = false;
      state.errorMessage = { text: 'Problem spliting cluster... Restoring', mode: 'api' };
      const existingPageId = state.pageId;
      if (existingPageId) {
        setTimeout(() => store.dispatch(fetchBlocks(existingPageId)), 2000);
      }
    });
    // #endregion

    // #region Delete cluster
    builder.addCase(deleteCluster.pending, (state, { meta }) => {
      state.editingClusterOrSection = true;
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(deleteCluster.fulfilled, (state, action) => {
      const remainingBlocks = state.blocks?.filter((block) => block.id !== action.payload.id);
      state.blocks = remainingBlocks;
      state.loading = false;
      state.error = undefined;
      state.errorMessage = undefined;
      state.success = true;
      state.selectedBlock = undefined;
      state.editingClusterOrSection = false;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(deleteCluster.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.editingClusterOrSection = false;
      state.errorMessage = { text: 'Problem deleting cluster... Restoring', mode: 'api' };
      const existingPageId = state.pageId;
      if (existingPageId) {
        setTimeout(() => store.dispatch(fetchBlocks(existingPageId)), 2000);
      }
    });
    // #endregion

    // #region Patch cluster
    builder.addCase(releaseCluster.pending, (state) => {
      state.editingClusterOrSection = true;
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(releaseCluster.fulfilled, (state, action) => {
      state.editingClusterOrSection = false;
      const stateBlocks = current(state.blocks) ?? [];
      const oldState = [...stateBlocks];

      const sections = oldState?.find((item) => item.id === action.payload.id)?.sections;

      let newItem: Block[] = [];

      sections?.forEach((item: Block) => {
        if (item.childBlocks && item.childBlocks?.length > 0) {
          newItem = [...newItem, ...item.childBlocks];
        }
      });

      const index: number | undefined = oldState?.findIndex(
        (item) => item.id === action.payload.id
      );
      if (index !== -1) {
        oldState.splice(index, 1);

        if (newItem.length > 0) {
          oldState.splice(index, 0, ...newItem);
        }
      }

      state.blocks = oldState;
      state.loading = false;
      state.error = undefined;
      state.success = true;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(releaseCluster.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.success = false;
    });
    // #endregion

    /****************** SECTIONS ******************/

    // #region Create Section
    builder.addCase(createSection.pending, (state) => {
      state.editingClusterOrSection = true;
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(createSection.fulfilled, (state, action) => {
      state.editingClusterOrSection = false;
      const stateBlocks = state.blocks ?? [];
      const oldBlocks = [...stateBlocks];
      const newCluster = action.payload;
      const indexBefore = oldBlocks.findIndex((b) => b.id === action.meta.arg.clusterId);
      oldBlocks.splice(indexBefore, 1, newCluster);

      state.blocks = oldBlocks;
      state.loading = false;
      state.error = undefined;
      state.success = true;

      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(createSection.rejected, (state) => {
      state.editingClusterOrSection = false;
      state.loading = false;
      state.error = true;
      state.success = false;
      state.errorMessage = { text: 'Problem creating section... Restoring', mode: 'api' };
      const existingPageId = state.pageId;
      if (existingPageId) {
        setTimeout(() => store.dispatch(fetchBlocks(existingPageId)), 2000);
      }
    });
    // #endregion

    // #region Delete section
    builder.addCase(deleteSection.pending, (state, { meta }) => {
      state.editingClusterOrSection = true;
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(deleteSection.fulfilled, (state, action) => {
      state.editingClusterOrSection = false;
      // at this point we are sure that the section removal runned ok, so the code is not strongly protected
      const stateBlocks = state.blocks ?? [];
      const pageBlocks = [...stateBlocks];
      const clusterToRemoveSectionFromIndex = stateBlocks.findIndex(
        (b) => b.id === action.meta.arg.clusterId
      );
      const clusterToRemoveSectionFrom = { ...pageBlocks[clusterToRemoveSectionFromIndex] };
      const sectionToRemoveIndex = clusterToRemoveSectionFrom.sections?.findIndex(
        (s) => s.id === action.meta.arg.sectionId
      );
      if (sectionToRemoveIndex) {
        const sectionToRemove = { ...clusterToRemoveSectionFrom.sections![sectionToRemoveIndex] };
        const foundBlocks = sectionToRemove.childBlocks! as Block[];
        const blocksToMove = foundBlocks || [];
        const updatedSectionBlocks = [
          ...clusterToRemoveSectionFrom.sections![sectionToRemoveIndex - 1]!.childBlocks!,
          ...blocksToMove,
        ];
        clusterToRemoveSectionFrom.sections![sectionToRemoveIndex - 1].childBlocks =
          updatedSectionBlocks;
        clusterToRemoveSectionFrom.sections!.splice(sectionToRemoveIndex, 1);
        pageBlocks.splice(clusterToRemoveSectionFromIndex, 1, clusterToRemoveSectionFrom);
        state.blocks = pageBlocks;
      }
      state.loading = false;
      state.error = undefined;
      state.errorMessage = undefined;
      state.success = true;
      state.selectedBlock = undefined;
      setTimeout(() => store.dispatch(resetSuccess()), 100);
    });
    builder.addCase(deleteSection.rejected, (state, action) => {
      state.editingClusterOrSection = false;
      state.loading = false;
      state.error = true;
      state.errorMessage = { text: 'Problem deleting section... Restoring', mode: 'api' };
      const existingPageId = state.pageId;
      if (existingPageId) {
        setTimeout(() => store.dispatch(fetchBlocks(existingPageId)), 2000);
      }
    });
    // #endregion

    /******************** AUX *********************/
    builder.addCase(resetSuccess, (state) => {
      state.success = undefined;
    });

    // TODO - use this instead of repeating this sames lines in all fullfilled request callback
    builder.addCase(cleanStateAfterSuccess, (state) => {
      state.loading = false;
      state.error = undefined;
      state.errorMessage = undefined;
      state.success = true;
    });
  },
});

export const {
  updateBlock,
  createBlock,
  removeBlock,
  updateBlocksList,
  switchDebugMode,
  updateBlocksListViaId,
  updateExercisesOrder,
  setHoverBlock,
  setTargetBlock,
  setFocusBlock,
  setErrorMessage,
  setExerciseToDuplicate,
  clearExerciseToDuplicate,
  setNextBlock,
  setBlockToDelete,
  setSelectedBlock,
  setBlockToScroll,
} = blocksSlice.actions;

export const { reducer: blocksReducer } = blocksSlice;
